import type {
  ConnectionDeleteResult,
  ConnectionListResult,
  ConnectionRepository,
  ConnectionVerifyResult,
  NormalizedConnection,
} from '@mint-lib/integration-registry';
import { useQuery, type QueryClient } from '@mint-lib/query-client';
import {
  OmniTraffickingIntegrationsTypes,
  PlatformUrns,
} from '@mint-sdk/api-integrations';
import { Integrations } from '@mint-sdk/api-omni-trafficking';

/**
 * `ConnectionRepository` for AppsFlyer, ported from the AdServer branch of
 * the legacy dispatch hooks (AppsFlyer is an `AdserverUrnEnum` member):
 * `useGetConnectionList.ts`, `useVerifyConnection.ts` and
 * `ManageConnection/useDeleteConnection.tsx` in `packages/connections`.
 *
 * Create/update are intentionally absent — they aren't part of the shared
 * `ConnectionRepository` contract (each platform's create/update payload
 * shape differs too much to unify), so `src/add-form` and `src/manage-form`
 * call `Integrations.createAppsFlyerIntegration()` /
 * `Integrations.updateAppsFlyerIntegration()` directly, same as the legacy
 * components did.
 */

// Deliberately not widened to `PlatformUrn` — this exact literal also has to
// satisfy `AdserverUrnType` (a narrower union) wherever it's passed to
// `Integrations.getIntegrationList`/`getIntegrationIsVerified` below.
export const APPSFLYER_URN = PlatformUrns.TRAFFICKING_PLATFORM_URN.appsflyer;

function normalizeConnection(
  item: OmniTraffickingIntegrationsTypes.OmniTraffickingIntegration,
): NormalizedConnection {
  return {
    id: item.id,
    name: item.name,
    platformUrn: APPSFLYER_URN,
    isVerified: item.isActive,
    isExpired: Boolean(item.expired),
    usingInAccounts: Boolean(item.usingInAccounts),
    userId: item.userId ?? null,
    extra: {
      androidId: item.extra.appsflyerAndroidId,
      iosId: item.extra.appsflyerIosId,
    },
  };
}

function useList(_params: { platform: string }): ConnectionListResult {
  const query = useQuery({
    ...Integrations.getIntegrationList({
      params: { adServer: APPSFLYER_URN },
    }),
  });

  return {
    connections: (query.data ?? []).map(normalizeConnection),
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

function useVerify({
  connectionId,
}: {
  platform: string;
  connectionId: string;
}): ConnectionVerifyResult {
  const query = useQuery({
    ...Integrations.getIntegrationIsVerified({
      params: {},
      urlParams: { integrationId: connectionId },
    }),
    enabled: Boolean(connectionId),
  });

  return {
    isVerified: query.data,
    isLoading: query.isLoading,
    isWrongCustomerError: false,
    refetch: query.refetch,
  };
}

function useDelete(): ConnectionDeleteResult {
  const { mutate, isPending } = Integrations.deleteIntegration();

  return {
    remove: (id, { onSuccess, onError }) => {
      mutate({ urlParams: { integrationId: id } }, { onSuccess, onError });
    },
    isPending,
  };
}

export const appsflyerConnectionRepository: ConnectionRepository = {
  useList,
  useVerify,
  useDelete,
};

/**
 * Loader-safe (non-hook) equivalent of `useList`, for `definition.ts`'s
 * `prefetch()` — router loaders run outside React, so they can't call the
 * `ConnectionRepository` hooks above.
 */
export async function fetchAppsFlyerConnections(
  queryClient: QueryClient,
): Promise<NormalizedConnection[]> {
  const items = await queryClient.ensureQueryData({
    ...Integrations.getIntegrationList({
      params: { adServer: APPSFLYER_URN },
    }),
  });

  return (items ?? []).map(normalizeConnection);
}
